import type { AdminJob, AdminJournalGroup } from '@fonus/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, OctagonX, RotateCcw, ScrollText, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Confirm,
  EmptyState,
  JobStatusBadge,
  ListSkeleton,
  PageTitle,
} from '../components/common.tsx';
import { ErrorCard } from '../components/ErrorCard.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.tsx';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import { api } from '../lib/api.ts';
import { describe, humanFromApi, humanFromJob } from '../lib/errors.ts';
import { credits, money, num, tashkent } from '../lib/format.ts';
import { useJournal } from '../lib/queries.ts';
import { go } from '../lib/router.ts';
import { newKey } from '../lib/utils.ts';

const TYPE: Record<string, string> = {
  GENERATE_STORY: 'Текст и рецензия',
  TTS: 'Озвучка',
  VOICE_SAMPLE: 'Подбор голосов',
};
const GROUP_TONE = { active: 'accent', failed: 'bad', done: 'ok', cancelled: 'neutral' } as const;
const GROUP_LABEL = {
  active: 'идёт',
  failed: 'есть ошибки',
  done: 'готово',
  cancelled: 'отменено',
} as const;

function JobRow({ j, canEdit, onChange }: { j: AdminJob; canEdit: boolean; onChange: () => void }) {
  const [showError, setShowError] = useState(false);
  const retry = useMutation({
    mutationFn: () =>
      api(`/admin/jobs/${j.id}/retry`, { method: 'POST', body: { idempotencyKey: newKey() } }),
    onSuccess: () => {
      toast.success('Повтор запущен');
      onChange();
    },
    onError: (e) => toast.error(humanFromApi(e).title),
  });
  const cancel = useMutation({
    mutationFn: () => api(`/admin/jobs/${j.id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.success(j.status === 'RUNNING' ? 'Остановится после текущего шага' : 'Отменено');
      onChange();
    },
    onError: (e) => toast.error(humanFromApi(e).title),
  });
  const active = j.status === 'QUEUED' || j.status === 'RUNNING';
  return (
    <>
      <TableRow>
        <TableCell className="whitespace-nowrap text-muted">{tashkent(j.createdAt)}</TableCell>
        <TableCell>
          {TYPE[j.type] ?? j.type}
          {j.lang ? ` · ${j.lang}` : ''}
          {j.retryLog.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {j.retryLog.map((r, i) => (
                <Badge key={i} tone="warn">
                  {i + 1 < j.retryLog.length || j.status !== 'FAILED'
                    ? `повтор ${String(i + 1)} из 2`
                    : 'попытки закончились'}{' '}
                  — {describe(r.code).title.toLowerCase()}
                </Badge>
              ))}
            </div>
          )}
        </TableCell>
        <TableCell>
          <JobStatusBadge
            status={j.status}
            stopping={j.cancelRequested && j.status === 'RUNNING'}
          />
          {j.status === 'FAILED' && (
            <button
              type="button"
              className="ml-2 text-[12px] text-accent"
              onClick={() => setShowError(!showError)}
            >
              {describe(j.errorCode, j.errorDetail ?? {}).title}
            </button>
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap text-right">
          {credits(j.ttsCredits)}
          <br />
          <span className="text-muted">{num(j.claudeInputTokens + j.claudeOutputTokens)} ток.</span>
        </TableCell>
        <TableCell className="whitespace-nowrap text-right">{money(j.money)}</TableCell>
        <TableCell className="text-right">
          {canEdit && (j.status === 'FAILED' || j.status === 'CANCELLED') && (
            <Button
              size="sm"
              variant="secondary"
              disabled={retry.isPending}
              onClick={() => retry.mutate()}
            >
              <RotateCcw /> Повторить
            </Button>
          )}
          {canEdit && active && !j.cancelRequested && (
            <Button
              size="sm"
              variant="ghost"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate()}
            >
              <X /> Отменить
            </Button>
          )}
        </TableCell>
      </TableRow>
      {showError && (
        <TableRow>
          <TableCell colSpan={6}>
            <ErrorCard error={humanFromJob(j)} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function Group({
  g,
  canEdit,
  onChange,
}: {
  g: AdminJournalGroup;
  canEdit: boolean;
  onChange: () => void;
}) {
  const [open, setOpen] = useState(g.status === 'active' || g.status === 'failed');
  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full flex-wrap items-center gap-3 p-4 text-left [&[data-state=open]>svg]:rotate-180">
          <span className="min-w-0 flex-1">
            <span className="font-semibold">{g.storyId ? `Сказка «${g.title}»` : g.title}</span>
            <span className="ml-2 text-[13px] text-muted">
              {g.jobs.length} {g.jobs.length === 1 ? 'задача' : 'задач'} · {tashkent(g.lastAt)}
            </span>
          </span>
          <Badge tone={GROUP_TONE[g.status]}>{GROUP_LABEL[g.status]}</Badge>
          <span className="text-[13px] text-muted">
            {credits(g.cost.ttsCredits)} ·{' '}
            {num(g.cost.claudeInputTokens + g.cost.claudeOutputTokens)} ток. · {money(g.cost.money)}
          </span>
          <ChevronDown className="size-4 text-muted transition" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-2 pb-3">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Когда</TableHead>
                  <TableHead>Задача</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead className="text-right">Расход</TableHead>
                  <TableHead className="text-right">Стоимость</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {g.jobs.map((j) => (
                  <JobRow key={j.id} j={j} canEdit={canEdit} onChange={onChange} />
                ))}
              </TableBody>
            </Table>
            {g.storyId && (
              <Button variant="link" size="sm" onClick={() => go(`stories/${g.storyId ?? ''}`)}>
                Открыть сказку
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export function Journal({ canEdit, isOwner }: { canEdit: boolean; isOwner: boolean }) {
  const [filter, setFilter] = useState<'all' | 'errors' | 'active'>('all');
  const q = useJournal(filter);
  const qc = useQueryClient();
  const [stopOpen, setStopOpen] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['journal'] });
    void qc.invalidateQueries({ queryKey: ['readiness'] });
  };
  const stop = useMutation({
    mutationFn: () =>
      api<{ cancelled: number; stopping: number }>('/admin/content/stop-all', { method: 'POST' }),
    onSuccess: (r) => {
      toast.success(
        `Остановлено: отменено ${String(r.cancelled)}, останавливается ${String(r.stopping)}. Пауза включена.`,
      );
      setStopOpen(false);
      refresh();
    },
  });
  return (
    <div>
      <PageTitle
        title="Журнал"
        subtitle={
          q.data?.fx
            ? `Курс: 1 $ = ${num(q.data.fx.rate)} сум${q.data.fx.source === 'cbu' ? ` (ЦБ, ${q.data.fx.date})` : ' (вручную)'}`
            : undefined
        }
        actions={
          isOwner && (
            <Button variant="destructive" onClick={() => setStopOpen(true)}>
              <OctagonX /> Остановить всё
            </Button>
          )
        }
      />
      <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
        <TabsList aria-label="Фильтр">
          <TabsTrigger value="all">Все</TabsTrigger>
          <TabsTrigger value="errors">Ошибки</TabsTrigger>
          <TabsTrigger value="active">Идут</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="mt-4 flex flex-col gap-3">
        {q.isLoading && <ListSkeleton />}
        {q.error && <ErrorCard error={humanFromApi(q.error)} />}
        {q.data?.groups.length === 0 && (
          <EmptyState
            icon={<ScrollText />}
            title="Пусто"
            hint={
              filter === 'all'
                ? 'Задачи появятся, когда вы создадите сказку или запустите подбор голосов.'
                : 'Под этот фильтр ничего не подходит.'
            }
          />
        )}
        {q.data?.groups.map((g) => (
          <Group key={g.key} g={g} canEdit={canEdit} onChange={refresh} />
        ))}
      </div>
      <Confirm
        open={stopOpen}
        onOpenChange={setStopOpen}
        title="Остановить всё?"
        description="Все задачи в очереди будут отменены, идущие остановятся после текущего шага, и включится пауза. Снять паузу можно на главной."
        confirmLabel="Остановить всё"
        destructive
        busy={stop.isPending}
        onConfirm={() => stop.mutate()}
      />
    </div>
  );
}
